import {
  any,
  array,
  bool,
  func,
  object,
  oneOf,
  oneOfType,
  string,
} from 'prop-types'
import React, { Component } from 'react'
import { Color } from '@opallabs/gem'
import { Button, Icon, Popover } from '@opallabs/lapidary'
import { AnalyticsLayer } from '@opallabs/quanta'
import Conditional from '../../../Conditional'
import { kuiperClassNames } from '../../../utils'
import TextInput from '../../TextInput'
import Month from '../Month'
import { parseDateForMonth } from './helpers'

export default class DatePickerPopover extends Component {
  static propTypes = {
    ariaLabelledBy: string,
    allowableDates: oneOfType([object, array]),
    classNames: any,
    date: object,
    dateFormat: string,
    disabled: bool,
    displayDate: bool,
    icon: string,
    iconPosition: oneOf(['right', 'left']),
    input: bool,
    onChange: func,
    onChangeDate: func,
    placeholder: string,
    popoverOptions: object,
    startOfWeek: oneOf([0, 1, 2, 3, 4, 5, 6]),
    text: oneOfType([object, string]),
    theme: oneOf(['dark', 'light']),
    weekStartsOn: oneOf([0, 1, 2, 3, 4, 5, 6]),
  }

  static defaultProps = {
    iconPosition: 'right',
    displayDate: true,
    startOfWeek: 0,
    theme: 'light',
  }

  constructor(props) {
    super(props)
    this.state = { popoverOpen: false }
  }

  closePopover = () => this.setState({ popoverOpen: false })
  togglePopover = () => this.setState({ popoverOpen: !this.state.popoverOpen })
  setPopoverOpen = (open) => this.setState({ popoverOpen: open })

  onDaySelect = (changer, date) => {
    changer({ due_date: date, due_date_raw: date.format('Y-MM-DD') })
    this.closePopover()
  }

  render() {
    const {
      ariaLabelledBy,
      allowableDates,
      date,
      dateFormat,
      disabled,
      icon,
      iconPosition,
      onChange,
      onChangeDate,
      placeholder,
      text,
      classNames,
      input,
      displayDate,
      theme,
    } = this.props
    const changer = onChangeDate || onChange
    const validDate = date && date.isValid()
    const dateText = validDate ? date.format(dateFormat) : placeholder

    // The `styleOverrides` are a hack to work around Kuiper's assumptions of
    // how components will be combined. The correct approach would be to use
    // flexbox, but that would require the icon to be inside the Kuiper input.
    //
    // The overrides are only relevant for the input-styled version of the
    // component--the button-styled version doesn't need the overrides.
    let iconStyleOverrides = {}
    if (displayDate && input) {
      iconStyleOverrides = {
        container: {
          insetBlockStart: 4,
          insetInlineStart: 4,
          position: 'absolute',
        },
      }
    }

    const lapidary_icon = (
      <Icon
        ariaMode="hidden"
        className="lapidary-icon"
        glyph={icon}
        theme={theme}
        styleOverrides={iconStyleOverrides}
        useHypernova={!/gem-/.test(icon)}
      />
    )
    const trigger = (
      <React.Fragment>
        <Conditional condition={displayDate}>{lapidary_icon}</Conditional>
        <Conditional condition={!displayDate}>
          <Button
            aria-expanded={this.state.popoverOpen}
            variant="ghost"
            buttonText="Choose date"
            hideButtonText={true}
            // onClick={this.togglePopover}
            icon={lapidary_icon}
            theme={theme}
          />
        </Conditional>
      </React.Fragment>
    )

    return (
      <AnalyticsLayer name="Date Picker Popover">
        <Popover.Root
          open={this.state.popoverOpen}
          onOpenChange={this.setPopoverOpen}
        >
          <Popover.Trigger>
            <div className={kuiperClassNames(classNames)} role="button">
              <Conditional condition={icon && iconPosition === 'left'}>
                {trigger}
              </Conditional>
              <Conditional condition={text}>
                <span className={kuiperClassNames({ disabled })}>{text}</span>
              </Conditional>
              <Conditional condition={!input && displayDate}>
                <span className={kuiperClassNames('date-text')}>
                  {dateText}
                </span>
              </Conditional>
              <Conditional condition={Boolean(input) && validDate}>
                <TextInput
                  ariaLabelledBy={ariaLabelledBy}
                  onChange={() => {}}
                  value={dateText}
                />
              </Conditional>
              <Conditional condition={icon && iconPosition === 'right'}>
                {trigger}
              </Conditional>
            </div>
          </Popover.Trigger>
          <Popover.Content style={{ zIndex: 9999 }}>
            <div css={{ backgroundColor: Color.light.Surface.N000 }}>
              <Month
                allowableDates={allowableDates}
                onDaySelect={this.onDaySelect.bind(this, changer)}
                value={parseDateForMonth(this.props.date)}
                startOfWeek={this.props.startOfWeek || this.props.weekStartsOn}
                highlighted={date}
                theme="light"
              />
            </div>
          </Popover.Content>
        </Popover.Root>
      </AnalyticsLayer>
    )
  }
}
DatePickerPopover.displayName =
  'Kuiper: DatePickerPopover ← Popover ← DatePicker ← Inputs'
